/**
 * IT Admin → IT training → one lesson (`/it-admin/training/:slug`, 8 Oct):
 * the one line, when to use it, numbered step cards (where to click in
 * Ivanti and in the HelpDesk), common mistakes, why it matters, and when it
 * was last updated. In the reader's language when a translation exists,
 * else English with a note.
 */

import { ArrowLeft, CircleX, GraduationCap, Icon, Target } from "@norican/icons";
import { Alert, Badge, Button, Card, type CardProps, Typography } from "@norican/react";
import { Link, useParams } from "react-router";

import { EmptyStateBlock } from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { LESSON_INDEX } from "./content";
import { LessonBlocks } from "./lesson-blocks";
import { findLesson, type LessonSection, type LessonStep } from "./lib/lessons";
import { EnglishOnlyNote, TOPIC_ICONS, useContentDate } from "./shared";

function BackLink() {
  const { t } = useT("tickets");
  return (
    <span>
      <Button
        variant="ghost"
        size="sm"
        render={<Link to="/it-admin/training" />}
        startIcon={<Icon icon={ArrowLeft} size="sm" />}
      >
        {t("itAdmin.training.back")}
      </Button>
    </span>
  );
}

function StepCard({ step, number }: { step: LessonStep; number: number }) {
  const { t } = useT("tickets");
  return (
    <li>
      <Card variant="outline" padding="md" className="flex gap-3">
        <span
          aria-hidden
          className="bg-accent text-fg-on-inverse flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
        >
          {number}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <Typography as="h4" variant="heading-6">
            <span className="sr-only">{t("itAdmin.training.step", { n: number })}: </span>
            {step.title}
          </Typography>
          <LessonBlocks blocks={step.blocks} />
        </div>
      </Card>
    </li>
  );
}

const SECTION_LOOK: Partial<
  Record<LessonSection["id"], { tone: NonNullable<CardProps["accentTone"]>; icon: typeof CircleX }>
> = {
  mistakes: { tone: "danger", icon: CircleX },
  why: { tone: "success", icon: Target },
};

function Section({ section, lang }: { section: LessonSection; lang: string }) {
  const { t } = useT("tickets");
  const heading =
    section.id === "other" ? section.heading : t(`itAdmin.training.section.${section.id}`);
  const look = SECTION_LOOK[section.id];
  const body =
    section.id === "steps" ? (
      <div lang={lang} className="flex flex-col gap-3">
        {section.blocks.length > 0 ? <LessonBlocks blocks={section.blocks} /> : null}
        <ol className="flex flex-col gap-3">
          {section.steps.map((step, index) => (
            <StepCard key={`${index}-${step.title}`} step={step} number={index + 1} />
          ))}
        </ol>
      </div>
    ) : (
      <div lang={lang}>
        <LessonBlocks blocks={section.blocks} />
      </div>
    );

  return (
    <section className="flex flex-col gap-3">
      {heading === "" ? null : (
        <Typography as="h3" variant="heading-5" className="flex items-center gap-2">
          {look === undefined ? null : <Icon icon={look.icon} size="sm" />}
          <span lang={section.id === "other" ? lang : undefined}>{heading}</span>
        </Typography>
      )}
      {look === undefined ? (
        body
      ) : (
        <Card variant="outline" padding="md" accent="left" accentTone={look.tone}>
          {body}
        </Card>
      )}
    </section>
  );
}

export function ItTrainingLessonPage() {
  const { t } = useT("tickets");
  const { slug = "" } = useParams();
  const { language } = useLanguage();
  const formatDate = useContentDate();
  const { data: me } = useMe();
  const entry = findLesson(LESSON_INDEX, slug, language);

  if (entry === null) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <EmptyStateBlock
          icon={<Icon icon={GraduationCap} size="lg" tone="accent" />}
          title={t("itAdmin.training.notFound")}
          description={null}
        />
      </div>
    );
  }

  const { lesson, fallback } = entry;
  return (
    <article className="flex max-w-3xl flex-col gap-6">
      <BackLink />

      <header className="flex flex-col gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone="accent" variant="subtle" size="sm">
            <span className="inline-flex items-center gap-1">
              <Icon icon={TOPIC_ICONS[lesson.topic]} size="xs" />
              {t(`itAdmin.training.topic.${lesson.topic}`)}
            </span>
          </Badge>
          {lesson.status === "draft" ? (
            <Badge tone="warning" variant="subtle" size="sm">
              {t("itAdmin.training.draft")}
            </Badge>
          ) : null}
        </span>
        <Typography as="h2" variant="heading-3" lang={lesson.lang}>
          {lesson.title}
        </Typography>
        <Typography as="p" variant="caption" tone="muted">
          <time dateTime={lesson.updated}>
            {t("itAdmin.training.updated", { date: formatDate(lesson.updated) })}
          </time>
        </Typography>
        {fallback ? <EnglishOnlyNote /> : null}
        {lesson.adminNote !== null && me?.role === "admin" ? (
          <Alert tone="warning" title={t("itAdmin.training.adminNote")}>
            <span lang={lesson.lang}>{lesson.adminNote}</span>
          </Alert>
        ) : null}
      </header>

      <div className="flex flex-col gap-6">
        <Card variant="subtle" padding="md" accent="left" accentTone="accent">
          <Typography as="p" variant="label" tone="muted">
            {t("itAdmin.training.section.summary")}
          </Typography>
          <Typography as="p" variant="body-lg" lang={lesson.lang} className="text-pretty">
            {lesson.summary}
          </Typography>
        </Card>

        {lesson.sections.map((section, index) => (
          <Section key={`${section.id}-${index}`} section={section} lang={lesson.lang} />
        ))}
      </div>
    </article>
  );
}
